import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import * as Icons from 'lucide-react';

export interface TrustSectionProps {
    config?: {
        title?: { en?: string; ar?: string };
        description?: { en?: string; ar?: string };
        backgroundImage?: string;
        items?: any[];
    };
}

export const TrustSection = ({ config }: TrustSectionProps = {}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const lang = i18n.language as 'en' | 'ar';

    const title = config?.title?.[lang] || config?.title?.en;
    const description = config?.description?.[lang] || config?.description?.en;
    const bgImage = config?.backgroundImage;

    const items = config?.items?.length ? config.items : [
        { id: '1', icon: 'Shield', title: { en: 'Verified Developer', ar: 'مطور معتمد' }, description: { en: 'Guaranteed success', ar: 'نجاح مضمون' } },
        { id: '2', icon: 'Award', title: { en: 'Premium Quality', ar: 'جودة عالية' }, description: { en: 'Best materials', ar: 'أفضل المواد' } },
        { id: '3', icon: 'Clock', title: { en: 'On Time Delivery', ar: 'تسليم في الموعد' }, description: { en: 'No delays', ar: 'بدون تأخير' } },
    ];

    return (
        <section
            className="py-12 sm:py-16 md:py-24 bg-surface relative"
            id="about"
            style={bgImage ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' } : {}}
        >
            {bgImage && <div className="absolute inset-0 bg-white/95" />}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
                {/* Optional CMS Header */}
                {(title || description) && (
                    <div className="text-center mb-12 sm:mb-16">
                        {title && (
                            <h2 className={clsx(
                                "text-3xl sm:text-4xl font-display font-bold text-secondary mb-4",
                                !isRTL && "uppercase"
                            )}>
                                {title}
                            </h2>
                        )}
                        {description && (
                            <p className="text-slate-500 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
                                {description}
                            </p>
                        )}
                    </div>
                )}

                {/* The HESN Standard */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12 text-center mb-12 sm:mb-16 md:mb-24">
                    {items.map((item: any, idx: number) => {
                        const Icon = item.icon && (Icons as any)[item.icon] ? (Icons as any)[item.icon] : Icons.Shield;
                        return (
                            <motion.div
                                key={item.id || idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.2 }}
                                viewport={{ once: true }}
                                className="flex flex-col items-center"
                            >
                                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-secondary mb-3 sm:mb-4" strokeWidth={1.5} />
                                <h3 className={clsx(
                                    "text-lg sm:text-xl font-display font-bold text-secondary mb-2 sm:mb-3",
                                    !isRTL && "uppercase"
                                )}>
                                    {item.title?.[lang] || item.title?.en}
                                </h3>
                                <p className="text-slate-500 leading-relaxed text-xs sm:text-sm max-w-xs">{item.description?.[lang] || item.description?.en}</p>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Construction Progress / Live Feed */}
                <div className="border-t border-slate-200 pt-8 sm:pt-12 md:pt-16">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-6 sm:mb-8 gap-4">
                        <div>
                            <h2 className={clsx(
                                "text-2xl sm:text-3xl font-display font-bold text-secondary mb-1 sm:mb-2",
                                !isRTL && "uppercase"
                            )}>
                                {t('trustSection.liveConstruction')}
                            </h2>
                            <p className="text-slate-500 text-sm sm:text-base">{t('trustSection.liveConstructionDesc')}</p>
                        </div>
                        <div className="flex items-center gap-2 text-red-500 font-bold animate-pulse text-sm">
                            <div className="w-2 h-2 bg-red-500 rounded-full" />
                            {isRTL ? 'مباشر' : 'LIVE UPDATES'}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="relative aspect-square bg-slate-200 rounded-hesn overflow-hidden group">
                                <img
                                    src={`https://images.unsplash.com/photo-1590486803833-1c5dc8ddd4c8?q=80&w=600&auto=format&fit=crop&bg=${i}`}
                                    alt="Construction Site"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
                                    loading="lazy"
                                />
                                <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 bg-black/70 text-white text-[8px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 sm:py-1 uppercase">
                                    {isRTL ? `موقع ${i} • منذ دقيقتين` : `Site ${i} • 2 mins ago`}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
